import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useGroup } from './GroupContext';
import api from '../services/api';
import { announce, setBadge, permissionState, requestPermission, popsUp, LOUD_LANES } from '../utils/browserNotify';
import { destinationOf } from '../nucleus/notifySpace';

const NotificationContext = createContext(null);

const POLL_INTERVAL = 30000; // 30 seconds
// While no tab of the inbox is visible, one hidden tab still polls — so the OS
// notification, the chime and the title badge still arrive — but three times as
// slowly: a background ping within a minute and a half, at a third of the cost.
export const HIDDEN_POLL_MS = POLL_INTERVAL * 3;
const PAGE_SIZE = 30;        // notifications loaded per page
// How long a new tab waits to hear from a tab already polling before it polls itself.
export const LEADER_WAIT = 250;
// The shape of what tabs say to each other. A tab running another build (an old
// tab left open over a deploy) speaks another version and is not listened to.
export const BUS_V = 1;
const EMPTY_COUNTS = { personal: 0, groups: {}, lanes: {} };

// This inbox is the person's, in the space they are standing in. Every request says
// so: without it the API falls back to the same defaults, but the developer portal
// runs an identical provider and used to be served this exact feed — polling it,
// and chiming for it, with no bell to show what rang.
const SURFACE = 'nucleus';

// ONE TAB POLLS. Every open tab used to poll every 30 s — hidden ones included —
// at two requests and some eight heavy queries a time. Now, of the tabs looking at
// the same inbox (person, space, lane), one leads: it polls and posts what it read
// on a BroadcastChannel, and the others show that. A visible tab outranks a hidden
// one, so the leader is a visible tab whenever there is one, polling every 30 s;
// when every tab is hidden one hidden tab leads at HIDDEN_POLL_MS, and the first
// tab to become visible takes over at once. A leader that is hidden or closed
// resigns; one that goes quiet is replaced after two of its intervals. Without
// BroadcastChannel every tab polls for itself, hidden ones at HIDDEN_POLL_MS.
function openBus(name) {
  try {
    return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(name) : null;
  } catch {
    return null;
  }
}

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

// What another tab says is checked before it is used: a row list that is not a
// list, or a count that is not a number, would otherwise take the whole app down
// when it renders. Anything not understood is ignored.
const isObject = (x) => !!x && typeof x === 'object' && !Array.isArray(x);
const isCounts = (c) => isObject(c)
  && (c.personal === undefined || typeof c.personal === 'number')
  && (c.groups === undefined || isObject(c.groups))
  && (c.lanes === undefined || isObject(c.lanes));
function isBusMessage(msg) {
  if (!isObject(msg) || msg.v !== BUS_V || typeof msg.from !== 'string') return false;
  if (msg.type === 'who' || msg.type === 'resign') return true;
  if (msg.type === 'result') {
    const d = msg.data;
    return isObject(d) && Array.isArray(d.notifications)
      && d.notifications.every((n) => isObject(n) && n.id != null)
      && (d.unread_count === undefined || typeof d.unread_count === 'number')
      && (d.total === undefined || d.total === null || typeof d.total === 'number')
      && typeof msg.limit === 'number'
      && (msg.counts == null || isCounts(msg.counts));
  }
  if (msg.type === 'patch') {
    const p = msg.patch;
    if (!isObject(p)) return false;
    if (p.op === 'all-read' || p.op === 'answered') return true;
    return (p.op === 'read' || p.op === 'dismiss') && p.id != null;
  }
  return false;
}

// Preload the notification sound
let notifAudio = null;
try {
  notifAudio = new Audio('/notification.wav');
  notifAudio.volume = 0.4;
} catch (e) {
  // Audio not supported
}

function playNotificationSound() {
  if (!notifAudio) return;
  // Clone so overlapping plays don't cut off
  const clone = notifAudio.cloneNode();
  clone.volume = 0.4;
  clone.play().catch(() => {}); // ignore autoplay restrictions
}

// A row read (or dismissed while unread) is one fewer in its lane and its space.
function countsLess(counts, row, space) {
  if (!counts || !row || row.is_read) return counts;
  const lanes = { ...(counts.lanes || {}) };
  if (row.lane && lanes[row.lane] > 0) lanes[row.lane] -= 1;
  const next = { ...counts, lanes };
  if (space === 'personal') next.personal = Math.max(0, (counts.personal || 0) - 1);
  else if (counts.groups && counts.groups[space] > 0) next.groups = { ...counts.groups, [space]: counts.groups[space] - 1 };
  return next;
}

// The lanes that may announce something: the loud ones, and Happened while its
// "This browser" switch is on.
function speakingLanes(prefs) {
  return popsUp(prefs, 'feed') ? new Set([...LOUD_LANES, 'feed']) : LOUD_LANES;
}

// What arrived in each speaking lane since the last read: the rise in its unread
// count, lane by lane. Another lane's rows are not in a lane-filtered list, so
// the count is the only sign one came.
function loudRises(before, after, shown, lanes) {
  const rises = {};
  lanes.forEach((l) => {
    if (l === shown) return;
    const d = (after?.lanes?.[l] || 0) - (before?.lanes?.[l] || 0);
    if (d > 0) rises[l] = d;
  });
  return rises;
}

// `navigate`, when given, is the router's: a click on an OS notification opens
// the row's item (RoutedNotificationProvider passes it). Without one the click
// only focuses the window, which is all a provider outside a router can do.
export const NotificationProvider = ({ children, navigate = null }) => {
  const { user } = useAuth();
  // The polling is keyed on WHO, not on the user object, which is a new object
  // whenever whoever holds it re-renders.
  const userKey = user ? String(user.id || user.user_id || 'me') : null;
  // Personal and group inboxes are separate, the way their trash had to be.
  const { activeGroupId, switchToPersonal } = useGroup();
  const space = activeGroupId || 'personal';
  // The click on an OS notification lands after renders this closure never saw.
  const openRef = useRef(null);
  openRef.current = (row) => {
    const to = destinationOf(row, space);
    if (!to?.url || !navigate) return;
    if (to.mustSwitchSpace && to.space === 'personal') switchToPersonal?.();
    navigate(to.url);
  };
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadedCountRef = useRef(0); // how many are currently loaded (for poll refresh + offset)
  const rowsRef = useRef([]);       // the rows as last set, for the local patches
  // The person's settings (GET /notifications/settings): the lane switches, Sound
  // and quiet hours every announcement is decided by. null until read, and the
  // server's defaults stand in meanwhile (browserNotify).
  const [prefs, setPrefs] = useState(null);
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const soundEnabled = prefs?.sound_enabled !== false;
  // Which lane the plate is showing. null is everything, which is what the
  // settings-window archive wants; the plate always names one.
  const [lane, setLane] = useState(null);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  // Bumped when what wants the person's hand may have changed — a poll brought
  // something new, or they answered something — so attention counts (attention.js)
  // are read again then, and not on every section change.
  const [attentionStamp, setAttentionStamp] = useState(0);
  const markAttentionStale = useCallback(() => setAttentionStamp((s) => s + 1), []);
  // The inbox (person, space, lane) this render is of: a fresh object whenever
  // any of the three changes, null when signed out. Every callback below closes
  // over the scope of the render that made it, so a read, or the patch a click
  // ends in, belongs to the inbox it was started on — even from a stale closure.
  // scopeRef is the inbox the tab is on now (set by the polling effect, which
  // also gives the scope its bus); work whose scope is no longer it is dropped:
  // not shown here, and not posted on the channel the tab has moved to.
  const scope = useMemo(() => (userKey ? { userKey, space, lane, bus: null } : null), [userKey, space, lane]);
  const scopeRef = useRef(null);
  const isCurrent = useCallback(() => scope !== null && scopeRef.current === scope, [scope]);
  const isFirstFetch = useRef(true);
  // Which rows this client has already announced. The old signal was "the unread
  // count went up", which chimes for somebody else's row arriving in a group, says
  // nothing when one arrives and another is read in the same poll, and cannot tell
  // the feed from the queue.
  const seenRef = useRef(new Set());
  const [permission, setPermission] = useState(() => permissionState());

  // ONE SOUND SWITCH, the server's. The Inbox kept its own in this browser
  // (notif_sound) while the settings page's was stored and never read; the two
  // disagreed. Both now write sound_enabled, and this is what plays by it.
  const toggleSound = useCallback(async () => {
    const before = prefsRef.current;
    const next = before?.sound_enabled === false;
    setPrefs({ ...before, sound_enabled: next });
    try {
      setPrefs(await api.updateNotificationSettings({ sound_enabled: next }));
    } catch {
      setPrefs(before); // put it back rather than lie
    }
  }, []);

  // Read on sign-in, and again whenever the tab comes back, so a change made in
  // another tab or on another device is what this one announces by.
  useEffect(() => {
    if (!userKey) { setPrefs(null); return undefined; }
    let live = true;
    const read = async () => {
      try {
        let data = await api.getNotificationSettings();
        // The old Inbox switch, carried over once: only "off" differs from the
        // server's default, and after that the browser's copy is forgotten.
        let legacy = null;
        try { legacy = localStorage.getItem('notif_sound'); } catch { /* none */ }
        if (legacy === 'false' && data.sound_enabled) {
          data = await api.updateNotificationSettings({ sound_enabled: false });
        }
        if (legacy !== null) { try { localStorage.removeItem('notif_sound'); } catch { /* fine */ } }
        if (live) setPrefs(data);
      } catch {
        // the defaults stand in until the next read
      }
    };
    read();
    const onVisible = () => { if (isVisible()) read(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userKey]);

  const setRows = useCallback((rows) => {
    rowsRef.current = rows;
    setNotifications(rows);
  }, []);

  // Announce rows: the chime once, the OS toast, and a click on the toast that
  // opens the row's item — it used to only bring the window forward, leaving
  // the person to find the thing it was about.
  const speakOf = useCallback((rows) => announce(rows, {
    prefs: prefsRef.current,
    playSound: playNotificationSound,
    onOpen: (row) => {
      try { window.focus(); } catch { /* not always allowed */ }
      openRef.current?.(row);
    },
  }), []);

  // Show one read of the list — this tab's own, or the leader's. Only a tab's own
  // read speaks (and browserNotify's claim keeps that to one tab per row).
  // The leader's read is sized to what the LEADER has loaded; a follower that has
  // opened more pages keeps them (`merge`), as its own poll would: the read
  // replaces the top of the list, and the rows below the last one it reached stay.
  const applyList = useCallback((data, limit, { speak, merge = false }) => {
    const newNotifs = data.notifications || [];
    const newUnread = data.unread_count || 0;
    let shown = newNotifs;
    // a read shorter than its limit is the whole list: nothing below it to keep
    if (merge && newNotifs.length >= limit) {
      const ids = new Set(newNotifs.map((n) => n.id));
      const own = rowsRef.current;
      let reached = -1;
      own.forEach((n, i) => { if (ids.has(n.id)) reached = i; });
      if (reached >= 0) shown = [...newNotifs, ...own.slice(reached + 1).filter((n) => !ids.has(n.id))];
    }
    setRows(shown);
    loadedCountRef.current = shown.length;
    setHasMore(typeof data.total === 'number' ? shown.length < data.total : newNotifs.length >= limit);
    setUnreadCount(newUnread);

    // What is new to THIS client, as opposed to what made a number go up.
    const fresh = newNotifs.filter((n) => !n.is_read && !seenRef.current.has(n.id));
    newNotifs.forEach((n) => seenRef.current.add(n.id));

    if (!isFirstFetch.current && fresh.length) {
      if (speak) {
        // Lane rules, one-sound-per-event and the OS toast all live in browserNotify.
        speakOf(fresh);
      }
      markAttentionStale();
    }
    isFirstFetch.current = false;
    setBadge(newUnread);
  }, [setRows, markAttentionStale, speakOf]);

  // Unread per space and per lane. The tabs and the space switcher both need a
  // number for something they are not currently showing, which the list cannot give.
  const readCounts = useCallback(async () => {
    if (!user) return null;
    try {
      return await api.getNotificationCounts({ surface: SURFACE, space });
    } catch {
      return null; // a count is a nicety; never let it break the shell
    }
  }, [user, space]);

  const fetchCounts = useCallback(async () => {
    const c = await readCounts();
    if (c) setCounts(c);
    return c;
  }, [readCounts]);

  // ANOTHER LANE'S NEWS. A lane-filtered list (the inbox on Feed) never holds
  // a queue or security row, so nothing new in those lanes was ever announced
  // while you looked elsewhere. When a loud lane's unread count rises, read the
  // top of the whole inbox and speak that many of its newest unseen rows — the
  // rise, not every unread row this client happens not to have seen.
  const loudCountsRef = useRef(null);
  const speakOtherLanes = useCallback(async (c) => {
    const before = loudCountsRef.current;
    if (c) loudCountsRef.current = c;
    if (!c || !before || !lane) return;
    const speaking = speakingLanes(prefsRef.current);
    const rises = loudRises(before, c, lane, speaking);
    if (!Object.keys(rises).length) return;
    let data;
    try {
      data = await api.getNotifications({ surface: SURFACE, space, limit: PAGE_SIZE, offset: 0 });
    } catch {
      return;
    }
    if (!isCurrent()) return;
    const fresh = [];
    (data?.notifications || []).forEach((n) => {
      if (rises[n.lane] > 0 && !n.is_read && !seenRef.current.has(n.id)) {
        rises[n.lane] -= 1;
        fresh.push(n);
      }
      if (speaking.has(n.lane)) seenRef.current.add(n.id);
    });
    if (fresh.length) { speakOf(fresh); markAttentionStale(); }
  }, [lane, space, isCurrent, speakOf, markAttentionStale]);

  // Refresh from the top, and the counts with it. Re-fetches at least one page, but
  // as many as are currently loaded, so a poll doesn't discard pages the user
  // already opened. What was read goes to the other tabs on the same inbox — and
  // only if this tab is still on it: a read of the personal inbox that answers
  // after the switch to a group is not shown, nor posted on the group's channel.
  const fetchNotifications = useCallback(async () => {
    if (!user || !isCurrent()) return;
    setLoading(true);
    try {
      const limit = Math.max(PAGE_SIZE, loadedCountRef.current);
      const data = await api.getNotifications({ surface: SURFACE, space, limit, offset: 0, ...(lane ? { lane } : {}) });
      if (!isCurrent()) return;
      applyList(data, limit, { speak: true });
      const c = await readCounts();
      if (!isCurrent()) return;
      if (c) setCounts(c);
      await speakOtherLanes(c);
      if (!isCurrent()) return;
      scope.bus?.post({ type: 'result', data, limit, counts: c });
    } catch (err) {
      // Silently fail — notification polling shouldn't break the app
    } finally {
      setLoading(false);
    }
  }, [user, space, lane, scope, isCurrent, applyList, readCounts, speakOtherLanes]);

  // Append the next page.
  const loadMore = useCallback(async () => {
    if (!user || !isCurrent()) return;
    setLoadingMore(true);
    try {
      const data = await api.getNotifications({ surface: SURFACE, space, limit: PAGE_SIZE, offset: loadedCountRef.current, ...(lane ? { lane } : {}) });
      if (!isCurrent()) return; // a page of an inbox no longer shown
      const more = data.notifications || [];
      loadedCountRef.current += more.length;
      setRows([...rowsRef.current, ...more]);
      setHasMore(typeof data.total === 'number' ? loadedCountRef.current < data.total : more.length >= PAGE_SIZE);
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingMore(false);
    }
  }, [user, space, lane, isCurrent, setRows]);

  // ── AFTER A CLICK, PATCH; DO NOT REFETCH ──────────────────────────────────
  // The server has already said yes; reading the list and the counts again to
  // learn what we just did cost three more requests per click. Each patch is
  // applied here and posted to the other tabs on the same inbox — the inbox of
  // the render the click was made in. One that answers after the tab moved to
  // another space (or person, or lane) is dropped: the old inbox's next poll
  // shows it, and the new inbox's rows were never touched.
  const applyPatch = useCallback((patch) => {
    const rows = rowsRef.current;
    if (patch.op === 'read' || patch.op === 'dismiss') {
      const row = rows.find((n) => n.id === patch.id);
      if (row && !row.is_read) {
        setUnreadCount((prev) => { const next = Math.max(0, prev - 1); setBadge(next); return next; });
        setCounts((c) => countsLess(c, row, space));
      }
      if (patch.op === 'read') {
        setRows(rows.map((n) => (n.id === patch.id ? { ...n, is_read: true } : n)));
      } else if (row) {
        setRows(rows.filter((n) => n.id !== patch.id));
        loadedCountRef.current = Math.max(0, loadedCountRef.current - 1);
      }
    } else if (patch.op === 'all-read') {
      setRows(rows.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      setBadge(0);
      setCounts((c) => {
        const lanes = Object.fromEntries(Object.keys(c?.lanes || {}).map((k) => [k, 0]));
        const next = { ...c, lanes };
        if (space === 'personal') next.personal = 0;
        else if (c?.groups && space in c.groups) next.groups = { ...c.groups, [space]: 0 };
        return next;
      });
    } else if (patch.op === 'answered') {
      markAttentionStale();
    }
  }, [space, setRows, markAttentionStale]);

  const patch = useCallback((p) => {
    if (!isCurrent()) return;
    applyPatch(p);
    scope.bus?.post({ type: 'patch', patch: p });
  }, [scope, isCurrent, applyPatch]);

  /** Local only: a row this client read (the request already succeeded). */
  const patchRead = useCallback((id) => patch({ op: 'read', id }), [patch]);
  /** Local only: a row this client dismissed. */
  const patchDismissed = useCallback((id) => patch({ op: 'dismiss', id }), [patch]);
  /** Local only: everything in this space read. */
  const patchAllRead = useCallback(() => patch({ op: 'all-read' }), [patch]);
  /** An ask was answered: the row is read, and attention counts are stale. */
  const patchAnswered = useCallback((id) => { patch({ op: 'read', id }); patch({ op: 'answered' }); }, [patch]);

  const markRead = useCallback(async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      patchRead(notificationId);
    } catch (err) {
      // Silently fail
    }
  }, [patchRead]);

  const markAllRead = useCallback(async () => {
    try {
      await api.markAllNotificationsRead({ surface: SURFACE, space });
      patchAllRead();
    } catch (err) {
      // Silently fail
    }
  }, [space, patchAllRead]);

  // Dismiss — the feed only. A queue row clears by being answered and a security
  // row is a record; the API refuses both, and the plate does not offer it.
  const dismiss = useCallback(async (notificationId) => {
    try {
      await api.dismissNotification(notificationId);
      patchDismissed(notificationId);
    } catch {
      // nothing removed, so nothing to put back
    }
  }, [patchDismissed]);

  // Polling: one leader per inbox, a visible tab when there is one.
  const pollRef = useRef(fetchNotifications);
  pollRef.current = fetchNotifications;
  const applyListRef = useRef(applyList);
  applyListRef.current = applyList;
  const applyPatchRef = useRef(applyPatch);
  applyPatchRef.current = applyPatch;

  useEffect(() => {
    if (!scope) {
      // Signed out: nothing of the last person's inbox stays for the next one —
      // not the rows, the counts, how far the list was opened, or what was announced.
      scopeRef.current = null;
      setRows([]);
      setUnreadCount(0);
      setCounts(EMPTY_COUNTS);
      setHasMore(false);
      loadedCountRef.current = 0;
      seenRef.current = new Set();
      return undefined;
    }
    isFirstFetch.current = true;
    loudCountsRef.current = null;
    const me = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const bus = openBus(`ofself-notifications:${userKey}:${space}:${lane || 'all'}`);
    let last = null;       // my latest result, for a tab that asks
    const post = (m) => {
      if (!bus) return;
      if (m.type === 'result') last = { data: m.data, limit: m.limit, counts: m.counts };
      // `hidden` is the sender's rank: a visible tab outranks a hidden one
      try { bus.postMessage({ ...m, v: BUS_V, from: me, hidden: !isVisible() }); } catch { /* closed */ }
    };
    scope.bus = bus ? { post } : null;
    scopeRef.current = scope;
    let leader = null;     // the tab that polls: me, another tab's id, or nobody known
    let leaderHidden = false;
    let leaderSeen = 0;
    let timer = null;
    let waiting = null;

    // Another tab leads, and has been heard from within two of its intervals.
    const leaderAlive = () => leader !== null && leader !== me
      && Date.now() - leaderSeen < (leaderHidden ? HIDDEN_POLL_MS : POLL_INTERVAL) * 2;
    // A visible tab outranks a hidden one; between equals the lower id does.
    const outranks = (id, hidden, other, otherHidden) => (hidden !== otherHidden ? !hidden : id < other);
    // Poll unless another tab leads — or, for a visible tab, unless a visible one does.
    const due = () => !leaderAlive() || (isVisible() && leaderHidden);
    const poll = () => { leader = me; return pollRef.current(); };
    const tick = () => { if (due()) poll(); };
    const stop = () => { if (timer) clearInterval(timer); timer = null; };
    const start = () => { stop(); timer = setInterval(tick, isVisible() ? POLL_INTERVAL : HIDDEN_POLL_MS); };
    const resign = () => {
      if (leader === me) {
        leader = null;
        post({ type: 'resign' });
      }
    };

    if (bus) {
      bus.onmessage = ({ data: msg }) => {
        if (!isBusMessage(msg) || msg.from === me) return;
        if (msg.type === 'result') {
          // Two leaders (both started at once, or a hidden one a visible tab is
          // taking over from): the higher rank keeps it. A follower goes with the
          // tab it hears unless it knows of a live leader that outranks it.
          const hidden = msg.hidden === true;
          const follow = leader === me
            ? outranks(msg.from, hidden, me, !isVisible())
            : !leaderAlive() || leader === msg.from || outranks(msg.from, hidden, leader, leaderHidden);
          if (follow) { leader = msg.from; leaderHidden = hidden; leaderSeen = Date.now(); }
          applyListRef.current(msg.data, msg.limit, { speak: false, merge: true });
          if (msg.counts) setCounts(msg.counts);
        } else if (msg.type === 'who') {
          if (leader === me && last) post({ type: 'result', ...last });
        } else if (msg.type === 'resign') {
          if (leader === msg.from) leader = null;
        } else if (msg.type === 'patch') {
          applyPatchRef.current(msg.patch);
        }
      };
    }

    const begin = () => {
      if (due()) poll();
      start();
    };
    if (bus) {
      // Is a tab already polling this inbox? It answers with what it last read.
      post({ type: 'who' });
      waiting = setTimeout(() => { waiting = null; begin(); }, LEADER_WAIT);
    } else {
      begin();
    }

    // Hidden: hand the lead to a visible tab, and slow down — if nobody takes it,
    // this tab (or another hidden one) leads again at HIDDEN_POLL_MS. Visible
    // again: read once (unless a visible tab is leading and keeping this one
    // current), then poll at the normal pace. During the first wait, begin decides.
    const onVisibility = () => {
      if (waiting !== null) return;
      if (!isVisible()) { resign(); start(); return; }
      begin();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (waiting) clearTimeout(waiting);
      stop();
      resign();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (bus) { try { bus.close(); } catch { /* already */ } }
      if (scopeRef.current === scope) scopeRef.current = null;
    };
    // scope changes with exactly userKey, space and lane; pollRef carries the latest fetch.
  }, [scope, userKey, space, lane, setRows]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      loadingMore,
      hasMore,
      soundEnabled,
      toggleSound,
      prefs,
      applyPrefs: setPrefs,
      fetchNotifications,
      loadMore,
      markRead,
      markAllRead,
      dismiss,
      patchRead,
      patchDismissed,
      patchAllRead,
      patchAnswered,
      markAttentionStale,
      attentionStamp,
      lane,
      setLane,
      counts,
      refreshCounts: fetchCounts,
      permission,
      askPermission: async () => { setPermission(await requestPermission()); },
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// The provider as the app mounts it, inside the router, so a click on an OS
// notification can open the row's item.
export const RoutedNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  return <NotificationProvider navigate={navigate}>{children}</NotificationProvider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// For a page that answers an ask outside the inbox (approving a proposal, accepting
// a follow request, answering an invitation): the attention counts are stale. Quiet
// outside a NotificationProvider, so such a page renders on its own too.
const noop = () => {};
export const useMarkAttentionStale = () => useContext(NotificationContext)?.markAttentionStale || noop;

export default NotificationContext;
